import { getSectionUpdatePathFromId, REQUEST_TYPES } from "@demandproai/shared";
import Constants from "../Constants";
import usePfqAxios from "./usePfqAxios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "../utils/helper";

const useDemandStudioEditActions = ({ caseId, onEditPersisted }) => {
    const { postData } = usePfqAxios();
    const [pendingEditActionCount, setPendingEditActionCount] = useState(0);
    const sendEditAction = useCallback(
        async (data, requestType = REQUEST_TYPES.MANUAL_EDIT) => {
            setPendingEditActionCount((count) => count + 1);
            try {
                const response = await postData(Constants.ApiUrl.createRequest,
                    JSON.stringify({
                        caseId,
                        data,
                        requestType
                    })
                );
                if (response?.success) {
                    onEditPersisted?.();
                }
                return response;
            } finally {
                setPendingEditActionCount((count) => count - 1);
            }
        },
        [caseId, postData, onEditPersisted]
    );

    const debouncedSendEditAction = useMemo(
        () => debounce(sendEditAction, 2000),
        [sendEditAction]
    );

    // flush any pending debounced call when the hook unmounts or the debounced fn is recreated
    useEffect(() => {
        return () => debouncedSendEditAction.flush();
    }, [debouncedSendEditAction]);

    const flushEditAction = useCallback(
        () => debouncedSendEditAction.flush(),
        [debouncedSendEditAction]
    );

    // persists immediately and resolves once saved so a flush before save/close is deterministic
    const bulkSectionUpdates = useCallback(
        async (sectionTexts) => {
            if (!caseId || !sectionTexts) return;
            const data = {};
            for (const [sectionId, text] of Object.entries(sectionTexts)) {
                const fieldPath = getSectionUpdatePathFromId(sectionId);
                if (!fieldPath) continue;
                data[fieldPath] = text;
            }
            await sendEditAction(data);
        }, [caseId, sendEditAction]
    );

    return {
        sendEditAction: debouncedSendEditAction,
        sendEditActionImmediate: sendEditAction,
        flushEditAction,
        bulkSectionUpdates,
        isSavingManualEdit: pendingEditActionCount > 0
    };
}

export default useDemandStudioEditActions;

